import { ReactNode } from "react";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";

interface AnimatedSectionProps {
  children: ReactNode;
  className?: string;
  animation?: "fade-up" | "fade-left" | "fade-right" | "scale" | "stagger";
  delay?: number;
}

export function AnimatedSection({ 
  children, 
  className,
  animation = "fade-up",
  delay = 0 
}: AnimatedSectionProps) {
  const { ref, isInView } = useInView({ threshold: 0.1 });

  const getAnimationClasses = () => {
    const baseTransition = "transition-all duration-700 ease-out";
    
    switch (animation) {
      case "fade-up":
        return cn(
          baseTransition,
          isInView 
            ? "opacity-100 translate-y-0" 
            : "opacity-0 translate-y-8"
        );
      case "fade-left":
        return cn(
          baseTransition,
          isInView 
            ? "opacity-100 translate-x-0" 
            : "opacity-0 -translate-x-8"
        );
      case "fade-right":
        return cn(
          baseTransition,
          isInView 
            ? "opacity-100 translate-x-0" 
            : "opacity-0 translate-x-8"
        );
      case "scale":
        return cn(
          baseTransition,
          isInView 
            ? "opacity-100 scale-100" 
            : "opacity-0 scale-95"
        );
      default:
        return cn(
          baseTransition,
          isInView 
            ? "opacity-100 translate-y-0" 
            : "opacity-0 translate-y-8"
        );
    }
  };

  return (
    <div 
      ref={ref} 
      className={cn(getAnimationClasses(), className)}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

interface StaggerContainerProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
}

export function StaggerContainer({ 
  children, 
  className,
  staggerDelay = 100 
}: StaggerContainerProps) {
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <div ref={ref} className={className}>
      {Array.isArray(children) 
        ? children.map((child, index) => (
            <div
              key={index}
              className={cn(
                "transition-all duration-500 ease-out",
                isInView 
                  ? "opacity-100 translate-y-0" 
                  : "opacity-0 translate-y-6"
              )}
              style={{ transitionDelay: `${index * staggerDelay}ms` }}
            >
              {child}
            </div>
          ))
        : children
      }
    </div>
  );
}
